import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { Language, getStoredLanguage, setStoredLanguage, t as translate, loadFullTranslations, I18nContextType } from "@/lib/i18n";

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
};

export const I18nProvider = ({ children }: { children: ReactNode }) => {
  const [lang, setLangState] = useState<Language>(getStoredLanguage());
  const [, setReady] = useState(false);

  // Lazily load full translations after initial render
  useEffect(() => {
    loadFullTranslations().then(() => setReady(true));
  }, []);

  useEffect(() => {
    const syncStoredLanguage = () => setLangState(getStoredLanguage());

    const originalSetItem = Storage.prototype.setItem;
    Storage.prototype.setItem = function patchedSetItem(key, value) {
      originalSetItem.call(this, key, value);
      if (key === "biletime-lang") window.dispatchEvent(new Event("biletime-lang-change"));
    };

    window.addEventListener("storage", syncStoredLanguage);
    window.addEventListener("biletime-lang-change", syncStoredLanguage);

    return () => {
      Storage.prototype.setItem = originalSetItem;
      window.removeEventListener("storage", syncStoredLanguage);
      window.removeEventListener("biletime-lang-change", syncStoredLanguage);
    };
  }, []);

  const setLang = useCallback((l: Language) => {
    setLangState(l);
    setStoredLanguage(l);
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>) => translate(key, lang, params),
    [lang]
  );

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
};
